import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, House, MagnifyingGlass } from "@phosphor-icons/react";

import { useAuth } from "@/context/AuthContext";
import { landingPathForUser } from "@/lib/personaNavigation";

export function NotFoundState({
  title = "Page not found",
  message = "The page may have moved, or you may not have access to it.",
  homeTo = "/",
  homeLabel = "Go to workspace",
  onBack,
}) {
  const navigate = useNavigate();
  return (
    <section
      data-testid="not-found"
      className="mx-auto flex min-h-[28rem] max-w-2xl flex-col items-center justify-center px-6 py-16 text-center"
    >
      <div className="flex h-14 w-14 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 shadow-sm">
        <MagnifyingGlass size={26} aria-hidden="true" />
      </div>
      <p className="mt-6 font-mono text-xs font-semibold uppercase tracking-[0.22em] text-slate-400">
        Error 404
      </p>
      <h1 className="mt-2 font-display text-3xl font-semibold tracking-tight text-slate-900">
        {title}
      </h1>
      <p className="mt-3 max-w-lg text-sm leading-6 text-slate-500">{message}</p>
      <div className="mt-7 flex flex-wrap justify-center gap-3">
        <button
          type="button"
          onClick={onBack || (() => navigate(-1))}
          className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-900 focus:ring-offset-2"
        >
          <ArrowLeft size={16} /> Go back
        </button>
        <Link
          to={homeTo}
          className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-900 focus:ring-offset-2"
        >
          <House size={16} /> {homeLabel}
        </Link>
      </div>
    </section>
  );
}

export default function NotFound() {
  const { user } = useAuth();
  const homeTo = landingPathForUser(user);

  useEffect(() => {
    const previous = document.title;
    document.title = "Page not found | BioLIMS";
    return () => { document.title = previous; };
  }, []);

  return (
    <NotFoundState
      homeTo={homeTo}
      message="We could not find that address. Check the URL, go back, or return to your workspace."
    />
  );
}
